import React, { useMemo } from 'react';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { useForm, FormProvider, type FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, ButtonText, HStack, Text, Pressable, Icon, Spinner,
} from '@gluestack-ui/themed';
import { ArrowLeft } from 'lucide-react-native';
import useMutateData from '@/admin/hooks/useMutateData';
import { useDialog } from '@/common/context/DialogContext';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import { createUserBody, updateUserBody } from '@/common/api/endpoints/users/users.zod';
import {
  createCohortCohortsPostBody,
  updateCohortCohortsCohortIdPutBody,
} from '@/common/api/endpoints/cohorts/cohorts.zod';
import { createCourseBody, updateCourseBody } from '@/common/api/endpoints/courses/courses.zod';
import { useGetCourse } from '@/common/api/endpoints/courses/courses';
import { useGetUser } from '@/common/api/endpoints/users/users';
import { useGetModule } from '@/common/api/endpoints/modules/modules';
import { useGetCohortCohortsCohortIdGet } from '@/common/api/endpoints/cohorts/cohorts';
import log from '@/common/utils/logger';
import { summarizeRhfFieldErrors } from '@/common/utils/summarizeRhfFieldErrors';
import { tabScreenOptions } from '@/admin/common/TabScreenOptions';
import type { AdminObjectName } from '@/admin/common/DataColumnConfig';

import UserDetailsForm from './User/UserDetailsForm';
import UserModulesReport from './User/UserModulesReport';
import UserNotificationsForm from './User/UserNotificationsForm';

import CourseDetailsForm from './Course/CourseDetailsForm';
import CourseEnrollmentsForm from './Course/CourseEnrollmentsForm';
import CourseModulesForm from './Course/CourseModulesForm';

import ModuleDetailsForm from './Module/ModuleDetailsForm';
import ModulePreviewForm from './Module/ModulePreviewForm';
import CohortDetailsForm from './Cohorts/CohortDetailsForm';

// Wrap strict schemas with .strip() to remove unknown fields instead of throwing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schemas: Record<string, any> = {
  users: {
    create: createUserBody.strip(),
    update: updateUserBody.strip(),
  },
  cohorts: {
    create: createCohortCohortsPostBody.strip(),
    update: updateCohortCohortsCohortIdPutBody.strip(),
  },
  courses: {
    create: createCourseBody.strip(),
    update: updateCourseBody.strip(),
  },
};

type TabConfig = {
  name: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
};

interface FormRouteParams {
  objectName: AdminObjectName;
  action?: 'create' | 'edit' | 'preview';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  title?: string;
  id?: string;
  isDocumentEditing?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormNavigator = ({ navigation, route }: { navigation: any, route: { params: FormRouteParams } }) => {
  const {
    objectName, action: routeAction, data: routeData, title: routeTitle, id: routeId, isDocumentEditing: routeIsDocumentEditing,
  } = route.params;

  const id = routeId || routeData?.id;
  const action = routeAction || (id ? 'edit' : 'create');

  // Courses default to document editing mode (Save doesn't close)
  const isDocumentEditing = routeIsDocumentEditing ?? (objectName === 'courses');

  // Use Orval-generated hooks for consistent data fetching
  const courseQuery = useGetCourse(id || '', {
    query: {
      // Always refetch by id for edit screens. Relying on route params alone is fragile with Fast Refresh.
      enabled: !!id && objectName === 'courses',
      select: (response) => response?.data?.data,
    },
  });

  const userQuery = useGetUser(id || '', {
    query: {
      enabled: !!id && objectName === 'users',
      select: (response) => response?.data?.data,
    },
  });

  const moduleQuery = useGetModule(id || '', undefined, {
    query: {
      enabled: !!id && objectName === 'modules',
      select: (response) => response?.data?.data,
    },
  });

  const cohortQuery = useGetCohortCohortsCohortIdGet(id || '', {
    query: {
      enabled: !!id && objectName === 'cohorts',
      select: (response) => response?.data?.data,
    },
  });

  // Select the appropriate query result based on objectName
  const fetchedQuery = ({
    courses: courseQuery,
    users: userQuery,
    modules: moduleQuery,
    cohorts: cohortQuery,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as Record<string, any>)[objectName] || { data: undefined, isLoading: false, error: undefined };

  const fetchedData = fetchedQuery.data;
  const fetchError = fetchedQuery.error;

  const hasRouteData = !!routeData && Object.keys(routeData).length > 0;
  const isFetching = fetchedQuery.isLoading && !hasRouteData;

  // Prefer server data when available. Route params can be empty or stale after Fast Refresh.
  // Memoize data to maintain stable reference for useForm initialization.
  const data = useMemo(() => {
    const result = fetchedData ?? (hasRouteData ? routeData : undefined);
    return result;
  }, [fetchedData, routeData, hasRouteData]);

  // Memoize defaultValues to prevent unnecessary recalculations.
  // Note: useForm only uses defaultValues on initial mount; subsequent changes
  // are handled by the key prop on FormProvider forcing a remount.
  const defaultValues = useMemo(() => {
    if (action === 'create' && objectName === 'users') {
      return { sendWelcomeEmail: true, includePassword: true, ...data };
    }
    return data || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, objectName, data?.id]);

  const methods = useForm({ defaultValues });

  const {
    watch, handleSubmit, reset, formState: { isDirty },
  } = methods;

  // Sync form state when server data is loaded or updated
  React.useEffect(() => {
    if (data && Object.keys(data).length > 0 && !isDirty) {
      reset(data);
    }
  }, [data, reset, isDirty]);

  // Stable key for form identity - when this changes, FormProvider remounts
  // with fresh state, eliminating the need for effect-based reset()
  const formKey = `${objectName}-${id || 'new'}`;

  // Derive display names
  const singularName = (
    {
      users: 'User',
      courses: 'Course',
      cohorts: 'Cohort',
      modules: 'Module',
      'modules/approvals': 'Approval',
      'reports/submissions': 'Submission',
    } as Record<string, string>
  )[objectName] || 'Record';

  // Derive record title based on object type
  const getRecordTitle = () => {
    if (action === 'create') {
      return `Create ${singularName}`;
    }

    const formData = watch();

    // Users: show academic title + full name
    if (objectName === 'users') {
      const parts = [formData.title, formData.firstname, formData.lastname].filter(Boolean);
      const name = parts.length > 0 ? parts.join(' ') : '';
      return name || singularName;
    }

    // Courses, modules, cohorts: use title field
    const { title } = formData;
    const fullTitle = title || routeTitle || singularName;

    // Truncate long titles to prevent header overflow (max 80 chars)
    if (fullTitle.length > 80) {
      return `${fullTitle.substring(0, 77)}...`;
    }

    return fullTitle;
  };
  const recordTitle = getRecordTitle();

  // Update navigation header title when recordTitle changes
  React.useEffect(() => {
    navigation.setOptions({
      title: recordTitle,
    });
  }, [navigation, recordTitle]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { createMutation, updateMutation } = useMutateData(objectName as AdminObjectName);
  const { t } = useTranslation();
  const { showToastMessage, showDialog, hideDialog } = useDialog();
  const { reportApiError } = useApiErrorReporter();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const executeSave = async (formData: any) => {
    try {
      // Parse through Zod schema to strip unknown fields and validate
      let payload = { ...formData };

      // Type coercion for numeric fields that come as strings from the form
      if (objectName === 'courses' && typeof payload.ects === 'string') {
        payload.ects = payload.ects.trim() === '' ? undefined : Number(payload.ects);
      }

      if (objectName === 'users') {
        // Never send back password on edit
        if (action === 'edit') {
          delete payload.password;
        }

        if (payload.trial) {
          // If not a trial, only send isTrial: false as per user request
          if (!payload.trial.isTrial) {
            payload.trial = { isTrial: false };
          } else {
            // Construct a clean trial update object to match UserTrialUpdateRequest
            // This ensures we don't send read-only/computed fields like trialActive
            const trialUpdate: Record<string, string | number | boolean | undefined> = {
              isTrial: true,
              message: payload.trial.message,
              ctaLabel: payload.trial.ctaLabel,
              ctaLink: payload.trial.ctaLink,
              durationDays: payload.trial.durationDays,
              startdate: payload.trial.startdate,
            };

            // Type coercion for durationDays
            if (typeof trialUpdate.durationDays === 'string') {
              trialUpdate.durationDays = trialUpdate.durationDays.trim() === ''
                ? undefined
                : Number(trialUpdate.durationDays);
            }

            // Transform simplified date to ISO string for Zod .datetime() validation
            const { startdate } = trialUpdate;
            if (typeof startdate === 'string' && !startdate.includes('T')) {
              const [year, month, day] = startdate.split('-').map(Number);
              const date = new Date(Date.UTC(year, month - 1, day));
              if (!Number.isNaN(date.getTime())) {
                trialUpdate.startdate = date.toISOString();
              }
            }
            payload.trial = trialUpdate;
          }
        }

        if (action === 'create') {
          const courseId = payload.course?.id;
          if (courseId) {
            payload.course = { id: courseId };
          } else {
            delete payload.course;
          }
        }
      }

      const schema = schemas[objectName]?.[action === 'create' ? 'create' : 'update'];
      if (schema) {
        payload = schema.parse(payload);
      }

      if (action === 'create') {
        const result = await createMutation.mutateAsync(payload);
        const resultData = result as { data?: { id?: string | number }, id?: string | number };
        const newId = resultData?.data?.id || resultData?.id;

        showToastMessage('success', `${singularName} created successfully`);

        // After successful creation, switch to edit mode to prevent duplicates and show extra tabs
        if (newId) {
          navigation.setParams({
            id: newId,
            action: 'edit',
            data: { ...formData, id: newId },
          });
        }
      } else if (action === 'edit') {
        await updateMutation.mutateAsync({ ...payload, id: data.id });
        showToastMessage('success', `${singularName} updated successfully`);
        reset(payload, { keepValues: true });
      }

      if (!isDocumentEditing) {
        navigation.goBack();
      }
    } catch (error) {
      reportApiError(error, `Failed to ${action} ${singularName}`);
    }
  };

  const onInvalid = (errors: FieldErrors) => {
    log.warn('Form validation failed:', errors);
    const { summary } = summarizeRhfFieldErrors(errors);
    const message = summary
      ? t('admin.forms.saveErrorWithDetails', { record: singularName, details: summary })
      : t('admin.forms.saveError');
    showToastMessage('error', message);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const checkMandatesChanged = (oldMandates: any, newMandates: any) => {
    // Helper to normalize ID (treat null, undefined, empty string as null)
    const getdId = (obj: { id?: string | number | null } | null) => (obj && obj.id ? obj.id : null);

    // Compare evaluation (corrector)
    const oldEvalId = getdId(oldMandates?.evaluation);
    const newEvalId = getdId(newMandates?.evaluation);
    if (oldEvalId !== newEvalId) return true;

    // Compare events
    const oldEvents = oldMandates?.events || {};
    const newEvents = newMandates?.events || {};

    // Get all unique event keys
    const allKeys = [...new Set([...Object.keys(oldEvents), ...Object.keys(newEvents)])];

    return allKeys.some((key) => {
      const oldEventId = getdId(oldEvents[key]);
      const newEventId = getdId(newEvents[key]);
      return oldEventId !== newEventId;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormSave = async (formData: any) => {
    const finalData = { ...formData };

    if (objectName === 'modules' && action === 'edit') {
      const mandatesChanged = checkMandatesChanged(data.mandates, finalData.mandates);
      if (mandatesChanged) {
        showDialog({
          title: 'Propagate Mandate Changes?',
          message:
            'You have changed the default mandates for this module. Do you want to apply these changes to all courses that currently use this module?',
          buttons: [
            {
              text: 'Yes, propagate',
              onPress: () => {
                hideDialog();
                executeSave({ ...finalData, propagateMandates: true });
              },
            },
            {
              text: "No, don't update courses",
              action: 'secondary',
              onPress: () => {
                hideDialog();
                executeSave({ ...finalData, propagateMandates: false });
              },
            },
          ],
        });
        return;
      }
    }
    await executeSave(finalData);
  };

  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      const screenMap: Record<string, string> = {
        users: 'Users',
        courses: 'Courses',
        cohorts: 'Cohorts',
        modules: 'Modules',
        'modules/approvals': 'Approvals',
        'reports/submissions': 'Submissions',
      };
      const screen = screenMap[objectName] || 'Users';
      navigation.navigate('Main', { screen });
    }
  };

  const Tab = createMaterialTopTabNavigator();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const objectTabs: Record<string, TabConfig[]> = {
    users: [
      { name: 'details', label: 'User Details', component: UserDetailsForm },
      ...(watch('role') === 'student' ? [{ name: 'grades', label: 'Modules & Grades', component: UserModulesReport }] : []),
      ...(watch('id') ? [{ name: 'notifications', label: 'Notifications', component: UserNotificationsForm }] : []),
    ],
    modules: [
      { name: 'details', label: 'Module Details', component: ModuleDetailsForm },
    ],
    'modules/approvals': [
      { name: 'preview', label: 'Module Preview', component: ModulePreviewForm },
    ],
    cohorts: [
      { name: 'details', label: 'Cohort Details', component: CohortDetailsForm },
    ],
    courses:
      action === 'create'
        ? [
          { name: 'details', label: 'Course Details', component: CourseDetailsForm },
        ]
        : [
          { name: 'details', label: 'Course Details', component: CourseDetailsForm },
          { name: 'modules', label: 'Modules', component: CourseModulesForm },
          { name: 'enrollments', label: 'Enrollments', component: CourseEnrollmentsForm },
        ],
  };

  if (objectName === 'modules/approvals') {
    return (
      <Box flex={1} bg="$backgroundLight0">
        <ModulePreviewForm
          moduleId={data?.id}
          version={data?.version}
          sourceVersion={data?.sourceVersion}
        />
      </Box>
    );
  }

  const CONTENT_WIDTH = 960;

  if (isFetching) {
    return (
      <Box flex={1} bg="$backgroundLight0" alignItems="center" justifyContent="center">
        <Spinner size="large" />
      </Box>
    );
  }

  if (fetchError && !hasRouteData) {
    return (
      <Box flex={1} bg="$backgroundLight0" alignItems="center" justifyContent="center">
        <Text color="$error600">
          Failed to load
          {' '}
          {singularName}
          . Please try again.
        </Text>
      </Box>
    );
  }

  return (
    <Box flex={1} bg="$backgroundLight0" alignItems="center">
      <FormProvider {...methods} key={formKey}>
        {/* Fixed-width centered container for everything */}
        <Box width={CONTENT_WIDTH} flex={1}>
          {/* Simplified Header: Back + Title + Actions on same line */}
          <HStack alignItems="center" justifyContent="space-between" py="$5">
            <HStack alignItems="center" space="md">
              <Pressable
                onPress={handleBack}
                p="$2"
                borderRadius="$md"
                sx={{
                  ':hover': { bg: '$backgroundLight100' },
                }}
              >
                <Icon as={ArrowLeft} size="md" color="$textSubtle" />
              </Pressable>
              <Text size="xl" fontWeight="semibold" color="$textHeading">
                {recordTitle}
              </Text>
            </HStack>

            {/* Actions */}
            {action !== 'preview' && (
              <HStack space="sm" alignItems="center">
                <Button size="sm" onPress={handleSubmit(handleFormSave, onInvalid)}>
                  <ButtonText>Save</ButtonText>
                </Button>
              </HStack>
            )}
          </HStack>

          {/* Tabs and Content */}
          <Box flex={1}>
            <Tab.Navigator
              id={`${objectName}Tabs`}
              screenOptions={{
                ...tabScreenOptions,
                animationEnabled: false,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                tabBarLabelStyle: {
                  ...tabScreenOptions.tabBarLabelStyle,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  textTransform: 'none' as any,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                tabBarItemStyle: {
                  ...tabScreenOptions.tabBarItemStyle,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  alignItems: 'center' as any,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any,
              }}
            >
              {objectTabs[objectName].map((tab) => (
                <Tab.Screen
                  name={tab.name}
                  key={tab.name}
                  component={tab.component}
                  options={{ title: tab.label }}
                  initialParams={{ data, action }}
                />
              ))}
            </Tab.Navigator>
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default FormNavigator;
